import type { Tour, Vehicle, CompanyInfo } from "../types";
import { SITE_URL, canonicalUrl, assetUrl } from "./siteConfig";

// TODO: When a real review source (Google Business Profile, on-site reviews,
// or a verified booking-platform feed) is available, plug AggregateRating into
// createBusinessStructuredData, createTourStructuredData, and createVehicleStructuredData.
// Per project decision F, do NOT emit AggregateRating until then — fabricated
// ratings risk a manual action.

export const createBusinessStructuredData = (companyInfo: CompanyInfo) => ({
  "@context": "https://schema.org",
  "@type": "TravelAgency",
  name: companyInfo.name,
  description: companyInfo.aboutSummary,
  url: SITE_URL,
  logo: `${SITE_URL}/svg/ikhlas-logo.svg`,
  contactPoint: {
    "@type": "ContactPoint",
    telephone: companyInfo.contactPhone,
    contactType: "customer service",
    availableLanguage: ["English", "Malay"],
  },
  address: {
    "@type": "PostalAddress",
    streetAddress: companyInfo.address,
    addressCountry: "NZ",
  },
  email: companyInfo.contactEmail,
  areaServed: ["NZ", "MY", "SG", "BN", "ID"],
  sameAs: [
    "https://www.facebook.com/ikhlastours",
    "https://www.tiktok.com/@ikhlastours",
    "https://wa.me/64221841601",
  ],
});

export const createTourStructuredData = (tour: Tour) => ({
  "@context": "https://schema.org",
  "@type": "TouristTrip",
  name: tour.title,
  description: tour.description,
  duration: `P${tour.duration}D`,
  offers: {
    "@type": "Offer",
    price: tour.price,
    priceCurrency: "NZD",
    availability: "https://schema.org/InStock",
  },
  provider: {
    "@type": "TravelAgency",
    name: "Ikhlas Tours",
    url: SITE_URL,
  },
  image: assetUrl(tour.featuredImage),
  touristType: ["Cultural Tourist", "Adventure Tourist"],
  itinerary: tour.itinerary.map((day: any) => ({
    "@type": "TouristDestination",
    name: day.title,
    description: day.activities
      .map((activity: any) => activity.name)
      .join(", "),
  })),
});

export const createVehicleStructuredData = (vehicle: Vehicle) => ({
  "@context": "https://schema.org",
  "@type": "Vehicle",
  name: vehicle.name,
  description: vehicle.description,
  vehicleModel: vehicle.name,
  vehicleType: vehicle.type === "car" ? "Car" : "Motorhome",
  offers: {
    "@type": "Offer",
    price: vehicle.pricePerDay,
    priceCurrency: "NZD",
    availability: vehicle.availability
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock",
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price: vehicle.pricePerDay,
      priceCurrency: "NZD",
      unitText: "day",
    },
  },
  provider: {
    "@type": "TravelAgency",
    name: "Ikhlas Tours",
    url: SITE_URL,
  },
  image: vehicle.images.map((img: string) => assetUrl(img)),
  seatingCapacity: vehicle.specs.passengers,
  fuelType: vehicle.specs.fuel,
});

export const createBreadcrumbStructuredData = (
  breadcrumbs: Array<{ name: string; url: string }>,
) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: breadcrumbs.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: canonicalUrl(item.url),
  })),
});

export const createWebsiteStructuredData = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Ikhlas Tours",
  url: SITE_URL,
  description:
    "New Zealand travel experts offering customisable itineraries, halal-friendly options, and round-the-clock support",
});

export const createFAQStructuredData = (
  faqs: Array<{ question: string; answer: string }>,
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});
